import * as React from 'react';
import { cn } from '@/lib/cn';
import { Skeleton } from './skeleton';
import type { ViewMode } from '@mdxeditor/editor';

const LazyEditorInner = React.lazy(() => import('./markdown-editor-inner'));

export interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  error?: string;
  hint?: string;
  placeholder?: string;
  className?: string;
  minHeight?: string;
  maxHeight?: string;
  /**
   * When true, the editor stretches to fill its flex parent's available
   * height (`flex-1 min-h-0`) and scrolls internally. `minHeight` still
   * applies as a floor when no flex parent is providing height.
   */
  fill?: boolean;
  diffMarkdown?: string;
  initialViewMode?: ViewMode;
  readOnly?: boolean;
  suppressHtmlProcessing?: boolean;
}

function MarkdownEditor({
  value,
  onChange,
  label,
  error,
  hint,
  placeholder,
  className,
  minHeight = '200px',
  maxHeight,
  fill,
  diffMarkdown,
  initialViewMode,
  readOnly,
  suppressHtmlProcessing,
}: MarkdownEditorProps) {
  const id = label?.toLowerCase().replace(/\s+/g, '-');
  const bounded = maxHeight !== undefined || !!fill;

  const editorElement = (
    <div
      className={cn(
        'mdx-editor-container rounded-md border border-input bg-background',
        bounded ? 'mdx-editor-container--bounded overflow-y-auto' : 'overflow-hidden',
        fill && 'flex flex-col flex-1 min-h-0',
        error && 'border-red-300',
        className,
      )}
      // In fill mode the flex parent owns the height — applying `minHeight`
      // here would force the editor past its allotted share and overflow.
      style={fill ? undefined : { minHeight, maxHeight }}
    >
      <React.Suspense
        fallback={
          <div className="p-3 space-y-2" style={{ minHeight }}>
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        }
      >
        <LazyEditorInner
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          diffMarkdown={diffMarkdown}
          initialViewMode={initialViewMode}
          readOnly={readOnly}
          suppressHtmlProcessing={suppressHtmlProcessing}
        />
      </React.Suspense>
    </div>
  );

  if (!label && !error && !hint) {
    return editorElement;
  }

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-foreground mb-1"
        >
          {label}
        </label>
      )}
      {editorElement}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      {hint && !error && (
        <p className="mt-1 text-sm text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}
MarkdownEditor.displayName = 'MarkdownEditor';

export { MarkdownEditor };
export default MarkdownEditor;
